import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import {
  Drawer,
  Loader,
  useGlobalUiStore,
  useHelpStore,
  useUserStore,
  ANALYTICS_EVENTS,
  useEvent,
} from '../../../common'
import {
  type HelpByCategoryList as IHelpByCategoryList,
  type HelpType,
  type InstitutionData,
  HelpCopy,
} from '../../../core'

import { HELP_BY_CATEGORY_LIST, POPULAR_TOPICS_LIST } from '../../../core/constants/Help'
import { ContactSupport } from './ContactSupport'
import { HelpByCategoryList } from './HelpByCategoryList'
import MissingInstitution from './MissingInstitution'
import { PopularTopicsDrawer } from './PopularTopicsDrawer'
import { ListItem, PopularTopicsList } from './PopularTopicsList'
import { RequestSupportFooter } from './RequestSupportFooter'
import { RequestSupportHeader } from './RequestSupportHeader'
import { RequestSupportInstitution } from './RequestSupportInstitution'
import SupportSuccessMessage from './SupportSuccessMessage'

interface RequestSupportProps {
  copy: HelpCopy
  isOpen: boolean
  onClose: () => void
}

interface TopicProps {
  isOpen: boolean
  topic: string
  type: HelpType
}

export const RequestSupport: React.FC<RequestSupportProps> = observer(
  ({ copy, isOpen, onClose }) => {
    const { onEvent } = useEvent()
    const { alert, createSupportTicket, isLoading, setAlert } = useHelpStore()
    const { isInitialized } = useGlobalUiStore()
    const { user } = useUserStore()

    const [institution, setInstitution] = React.useState('')
    const [isContactSupportDrawerOpen, setIsContactSupportDrawerOpen] = React.useState(false)
    const [isMissingInstitutionsOpen, setIsMissingInstitutionsOpen] = React.useState(false)
    const [topicsListDrawer, setTopicsListDrawer] = React.useState<TopicProps>({
      isOpen: false,
      topic: '',
      type: undefined,
    })

    const helpByCategoryList = HELP_BY_CATEGORY_LIST.map((el) => ({
      ...el,
      title: copy.help_by_category_list[el.guid as keyof IHelpByCategoryList].label,
      topics: el.topics.map(({ type }, index) => ({
        type: type as HelpType,
        label: copy.help_by_category_list[el.guid as keyof IHelpByCategoryList].topics[index],
      })),
    }))

    const popularTopics = Object.keys(copy.popular_topics_list).map((list, i) => ({
      name: copy.popular_topics_list[list],
      type: POPULAR_TOPICS_LIST[i].type as ListItem['type'],
    }))

    const handleCloseDrawer = () => {
      setInstitution('')
      setTopicsListDrawer({ isOpen: false, topic: '', type: undefined })
    }

    const handleContactSupportSubmit = (message: string) => {
      if (!user.email) return

      createSupportTicket({
        email: user.email,
        message: institution ? `Institution: ${institution}, ${message}` : message,
        title: topicsListDrawer.topic || 'Contact Support',
      })
    }

    const handleSupportNavigation = (topic: string, type: HelpType) => {
      if (type === 'other') setIsMissingInstitutionsOpen(true)
      else setTopicsListDrawer({ isOpen: true, topic, type })
    }

    const handleSupportSuccessMessage = () => {
      setAlert('')
      setIsContactSupportDrawerOpen(false)
      setIsMissingInstitutionsOpen(false)
      handleCloseDrawer()
    }

    const handleMissingInstitution = (data: InstitutionData) => {
      if (!user.email) {
        setAlert(copy.request_support_email_not_exists_message, 'error')
        return
      }

      onEvent(ANALYTICS_EVENTS.HELP_CLICK_SUPPORT_FOR_MISSING_INSTITUTION, {
        supportTopic: 'missingInstitution',
        institutionName: data.institutionName,
        institutionWebsite: data.institutionWebsite,
        institutionLoginPage: data.institutionLoginPage,
      })

      createSupportTicket({
        email: user.email,
        message: `Institution Website: ${data.institutionWebsite}${data.institutionLoginPage ? ` Institution Login Page: ${data.institutionLoginPage}` : ''} `,
        title: `Institution Request: ${data.institutionName}`,
      })
    }

    return (
      <Drawer isOpen={isOpen} onClose={onClose} title={copy.request_support}>
        {!isInitialized || isLoading ? (
          <Loader />
        ) : (
          <React.Fragment>
            <Stack>
              <RequestSupportHeader
                subtitle={copy.request_support_short_content}
                title={copy.request_support_title}
              />
              <Text
                color="text.primary"
                fontWeight="bold"
                gutterBottom={true}
                sx={{ ml: 24 }}
                variant="caption"
              >
                {copy.popular_topics}
              </Text>
              <PopularTopicsList
                list={popularTopics}
                onClick={(title, type) => {
                  onEvent(ANALYTICS_EVENTS.HELP_CLICK_SUPPORT_TOPIC, { supportTopic: title })

                  handleSupportNavigation(title, type)
                }}
                sx={{ fontWeight: 400 }}
              />
              <Text
                color="text.primary"
                fontWeight="bold"
                gutterBottom={true}
                sx={{ m: '24px 0px 0px 24px' }}
                variant="caption"
              >
                {copy.help_by_category}
              </Text>
              <HelpByCategoryList
                helpByCategory={helpByCategoryList}
                onClick={handleSupportNavigation}
              />
              <Box sx={{ p: 24 }}>
                <RequestSupportFooter
                  copy={copy}
                  onButtonClick={() => {
                    onEvent(ANALYTICS_EVENTS.HELP_OPEN_SUPPORT_REQUEST, {})

                    setIsContactSupportDrawerOpen(true)
                  }}
                />
              </Box>
            </Stack>
            <ContactSupport
              alert={alert}
              copy={copy}
              email={user.email}
              institution={institution}
              isLoading={isLoading}
              isOpen={isContactSupportDrawerOpen}
              onClose={() => {
                setIsContactSupportDrawerOpen(false)
                setInstitution('')
              }}
              onSubmit={handleContactSupportSubmit}
              setAlert={setAlert}
              topic={topicsListDrawer.topic || copy.contact_support}
            />
            <MissingInstitution
              copy={copy}
              isOpen={isMissingInstitutionsOpen}
              onClose={() => setIsMissingInstitutionsOpen(false)}
              onSave={handleMissingInstitution}
            />
            <PopularTopicsDrawer
              copy={copy}
              isOpen={topicsListDrawer.isOpen}
              onClick={(title) => {
                setInstitution(title)
                if (topicsListDrawer.type === 'internal') setIsContactSupportDrawerOpen(true)
              }}
              onClose={handleCloseDrawer}
              topic={topicsListDrawer.topic}
            />
            <SupportSuccessMessage
              copy={copy}
              email={user.email!}
              isOpen={!!alert.message && alert.severity === 'success' && !isLoading}
              onBack={() => {
                setAlert('')
                if (topicsListDrawer.type === 'internal') setIsContactSupportDrawerOpen(false)
              }}
              onClose={handleSupportSuccessMessage}
            />
            <RequestSupportInstitution
              copy={copy}
              institution={institution}
              isOpen={!!institution && topicsListDrawer.type === 'external'}
              onClose={() => setInstitution('')}
              popularTopicsDrawerOnClose={handleCloseDrawer}
              title={topicsListDrawer.topic}
            />
          </React.Fragment>
        )}
      </Drawer>
    )
  },
)
